import React from 'react';

import { AutocompleteableAddressFields } from '.';
import { Autocomplete, TextField } from '@mui/material';
import { debounce } from 'lodash';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import SmartyStreetsSDK, {
  SuggestionUSAutocompletePro,
} from 'smartystreets-javascript-sdk';

interface Props {
  label: string;
  onSelect: (fieldValues: AutocompleteableAddressFields) => void;
  onChange: (...event: any[]) => void;
  errorMessage?: string;
}

/**
 * Helper component to render the smarty streets typeahead for delivery address form.
 *
 * We can refactor to make more generic and slot in a different typeahead mechanism if need ever arises.
 */
const TypeaheadTextField: React.FC<Props> = ({
  label,
  errorMessage,
  onChange,
  onSelect,
}) => {
  /**
   * Smartystreets setup
   */
  const SmartyStreetsCore = SmartyStreetsSDK.core;
  const { Lookup } = SmartyStreetsSDK.usAutocompletePro;
  const { publicRuntimeConfig } = getNextConfigValues();
  const { SMARTYSTREETS_CLIENT_KEY, SMARTYSTREETS_HOST } = publicRuntimeConfig;
  const credentials = new SmartyStreetsCore.SharedCredentials(
    SMARTYSTREETS_CLIENT_KEY,
    SMARTYSTREETS_HOST,
  );
  const client = SmartyStreetsCore.buildClient.usAutocompletePro(credentials);

  const [smartySuggestions, setSmartySuggestions] = React.useState<
    SuggestionUSAutocompletePro[]
  >([]);

  const debouncedAddress = React.useCallback(
    debounce((addressLine1) => smartyLookup(addressLine1), 300),
    [],
  );

  // this is what the typeahead will actually display
  const [displayText, setDisplayText] = React.useState('');

  const [value, setValue] = React.useState<SuggestionUSAutocompletePro | null>(
    null,
  );

  const smartyLookup = async (address: string) => {
    if (address.length < 3) return;
    const lookup = new Lookup(address);
    lookup.maxResults = 5;
    try {
      const { result } = await client.send(lookup);
      setSmartySuggestions(result);
    } catch (err: any) {
      setSmartySuggestions([]);
      logger.error({
        msg: 'Smarty typeahead request failed',
        noticable: err,
      });
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={smartySuggestions}
      getOptionLabel={(option) => {
        // result _should_ always be instance of Suggestion, but the type technically
        // allows for string as well in which case we should just return the string.
        return typeof option === 'string'
          ? option
          : `${option.streetLine} ${option.city}, ${option.state}`;
      }}
      inputValue={displayText}
      onInputChange={(e, value, reason) => {
        // only update here if change was user input.
        // - displayText should not be updated from programmatic changes, which would cause city and state to appear
        //    since they come from the label (we only want the address line)
        // - smarty lookup should only be called when user changes, otherwise we get a dupe lookup when user selects an address
        if (reason === 'input') {
          // call onChange passed down from react-hook-form so that address is updated by user typing --- this allows us to use
          // what the user typed in if they bypass autocomplete, but the autocomplete will replace value here if they do use that.
          onChange(e);
          setDisplayText(value);
          debouncedAddress(value ?? '');
        }
      }}
      value={value}
      onChange={(e, newValue) => {
        if (newValue === null) {
          setDisplayText('');
          onSelect({
            addressLine1: '',
          });
        }
        // don't think we actually get string types but this keeps TS checker happy
        else if (typeof newValue !== 'string') {
          setValue(newValue);
          setDisplayText(newValue.streetLine);
          onSelect({
            addressLine1: newValue.streetLine,
            city: newValue.city,
            state: newValue.state,
            zipCode: newValue.zipcode,
          });
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!errorMessage}
          helperText={errorMessage}
        />
      )}
    />
  );
};

export default TypeaheadTextField;
